// Angular
import { computed, Injectable, signal } from '@angular/core';

// RxJS
import { BehaviorSubject, filter, map } from 'rxjs';

// Local
import { EmulatedProfile } from '@app/model/emulation.model';
import { UserTypeEnum } from '@app/model/user-types.model';
import { UserGroup } from '@app/types/user/user-permissions';

/**
 * The possible organization types as taken from the database
 * (will break if those strings change)
 */
export type OrganizationType =
  | 'FileandServeXpress'
  | 'Law Firm'
  | 'Court'
  | '3rd party'
  | 'Public Access'
  | 'Self-Represented - Organization (SRO)'
  | 'Self-Represented - Individual (SRI)'
  | 'Name TBD';

/**
 * Organization type ids as stored in the database, from the `fsx_organization_type_id` claim.
 * Only the types the client has to tell apart are listed.
 *
 * Preferred over {@link OrganizationType} wherever the distinction drives behaviour: the ids are
 * stable, the display strings are not.
 */
export enum OrganizationTypeId {
  SelfRepresentedOrganization = 5,
  SelfRepresentedIndividual = 6,
}

export interface IdentityClaims {
  /**
   * The authentication context class reference.
   * Defines the level of authentication assurance used (e.g., MFA).
   */
  acr: string;

  /**
   * The hash of the access token.
   * Used to validate the access token's integrity.
   */
  at_hash: string;

  /**
   * The audience for which the token is intended.
   * Typically the client ID of the application.
   */
  aud: string;

  /**
   * The time the user was authenticated.
   * Represented as a UNIX timestamp (seconds since epoch).
   */
  auth_time: number;

  /**
   * The email address of the logged-in user.
   */
  email: string;

  /**
   * The expiration time of the token.
   * Represented as a UNIX timestamp.
   */
  exp: number;

  /**
   * The courthouse ID associated with the logged-in user.
   */
  extension_fsx_court_house_id: string;

  /**
   * The logged-in user's own id in C-Track. Issued for Form 50 Agents; optional because a
   * user type that has no C-Track presence has no such claim.
   */
  extension_fsx_efm_id?: string;

  /**
   * A comma-separated list of security functions assigned to the user.
   */
  extension_security_functions: string;

  /**
   * The last name (surname) of the logged-in user.
   */
  family_name: string;

  /**
   * The organization to which the logged-in user belongs.
   */
  fsx_organization: string;

  /**
   * The unique ID of the organization (Court) the user belongs to.
   */
  fsx_organization_id: string;

  /**
   * The type of organization the user is associated with.
   */
  fsx_organization_type: OrganizationType;

  /**
   * The unique ID representing the organization type.
   */
  fsx_organization_type_id: string;

  /**
   * The user type classification (e.g., Admin, Judge, Clerk).
   */
  fsx_user_type: UserTypeEnum;

  /**
   * The first name (given name) of the logged-in user.
   */
  given_name: string;

  /**
   * The ID of the access group the logged-in user belongs to.
   */
  group_id: string;

  /**
   * The time at which the token was issued.
   * Represented as a UNIX timestamp.
   */
  iat: number;

  /**
   * Indicates whether the user is in the process of resetting their password.
   */
  isForgotPassword: boolean;

  /**
   * The issuer of the token (typically the identity provider URL).
   */
  iss: string;

  /**
   * The middle name of the logged-in user (if available).
   */
  middle_name: string;

  /**
   * The full name of the logged-in user.
   */
  name: string;

  /**
   * The "not before" time for the token.
   * The token is not valid before this time (UNIX timestamp).
   */
  nbf: number;

  /**
   * A unique value used to mitigate replay attacks.
   * It helps associate authentication requests with token responses.
   */
  nonce: string;

  /**
   * The object ID of the user (often from Azure AD or other IdPs).
   * Uniquely identifies the user across the identity system.
   */
  oid: string;

  /**
   * The username or sign-in name used by the logged-in user.
   */
  signinname: string;

  /**
   * The subject identifier for the user.
   * A unique user ID assigned by the identity provider.
   */
  sub: string;

  /**
   * The version of the token format.
   */
  ver: string;
}

/**
 * A type representing the keys of the IdentityClaims interface.
 */
export type IdentityClaimKeys = keyof IdentityClaims;

@Injectable({
  providedIn: 'root',
})
export class IdentityClaimsService {
  readonly userId = signal<string | null>(null);
  readonly orgId = signal<string | null>(null);

  /** The logged-in user's user type (e.g. 'SR' for self-represented). */
  readonly userType = signal<UserTypeEnum | null>(null);

  /** The logged-in user's organization type (e.g. SRI / SRO). */
  readonly organizationType = signal<OrganizationType | null>(null);

  /**
   * The logged-in user's organization type id, from the `fsx_organization_type_id` claim. Null when
   * the claim is absent or not a number.
   *
   * Self-represented users of both kinds share one user type (`SR`), so this is the only thing that
   * separates a self-represented organization (5) from a self-represented individual (6).
   */
  readonly organizationTypeId = signal<OrganizationTypeId | null>(null);

  /**
   * The effective user's access-group id, from the `group_id` claim. Court users (clerks and
   * judges) are group `2`; law-firm users are group `1`.
   *
   * Normalized to a string on the way in — the claim is typed `string` but the token can carry the
   * group as a number, which would slip past a strict comparison against {@link UserGroup} (#172611).
   */
  readonly groupId = signal<UserGroup | null>(null);

  /**
   * The effective user's own C-Track id, from the `extension_fsx_efm_id` claim. Null for any user
   * type that has no C-Track presence.
   *
   * Used by Certificate Entry, where the Form 50 Agent is themselves the party, to supply the
   * efmId the backend requires on that party.
   *
   * **Null while emulating, deliberately** — see {@link applyEmulation}.
   */
  readonly efmId = signal<string | null>(null);

  /**
   * True when the logged-in user is a self-represented individual, determined by
   * either a Self-Represented user type.  Used by filing forms to default to an
   * Individual party and hide the Find-a-Party and Representation sections.
   */
  readonly isSelfRepresentedIndividual = computed<boolean>(
    () => this.userType() === UserTypeEnum.SelfRepresented,
  );

  /**
   * True when the effective user is a self-represented **organization** (org type 5). Shares the
   * `SR` user type with a self-represented individual, so {@link isSelfRepresentedIndividual} is
   * true for these users too — only the organization type tells the two apart.
   *
   * An SRO files under its own authorizers like any other organization, so it must not be treated
   * as a single-authorizer user (#172958).
   */
  readonly isSelfRepresentedOrganization = computed<boolean>(
    () =>
      this.userType() === UserTypeEnum.SelfRepresented &&
      (this.organizationTypeId() ===
        OrganizationTypeId.SelfRepresentedOrganization ||
        // Fall back to the display string when the id claim is absent.
        (this.organizationTypeId() === null &&
          this.organizationType() === 'Self-Represented - Organization (SRO)')),
  );

  /**
   * True when the effective user belongs to a court — clerks and judges alike. Emulation-aware, as
   * {@link applyEmulation} overlays `group_id` here. Deliberately diverges from
   * `AuthService.isCourtUser`, which reads the raw OAuth claims and is **not** emulation-aware —
   * this is the signal-based reading of the same claim, for use inside computeds, that reflects
   * the emulated user while an admin is emulating.
   */
  readonly isCourtUser = computed<boolean>(
    () => this.groupId() === UserGroup.Court,
  );

  /**
   * True when the effective user is a court Clerk. Emulation-aware: while an admin is emulating,
   * applyEmulation overlays fsx_user_type here, so this reflects the emulated user's type. Used by
   * the OPF new-case party form to expose the "no identified representation" toggle to clerks only.
   */
  readonly isClerk = computed<boolean>(
    () => this.userType() === UserTypeEnum.Clerk,
  );

  /**
   * True when the effective user is a Form 50 Agent. Emulation-aware (reads the emulation-overlaid
   * user type), so it honors an admin emulating a Form 50 Agent. Used to tailor the filing and
   * Organization Settings experience for the Form 50 Agent user type.
   */
  readonly isForm50Agent = computed<boolean>(
    () => this.userType() === UserTypeEnum.Form50Agent,
  );

  /**
   * True when the effective user always files under a single authorizer: self-represented
   * individuals and Form 50 Agents are their own authorizer, so the court returns exactly one
   * option. Filing screens auto-select that option and hide the authorizer picker.
   *
   * A self-represented *organization* is excluded: it shares the `SR` user type with an individual
   * but has its own authorizers to choose from, and hiding the picker left one of them selected by
   * accident (#172958).
   */
  readonly isSingleAuthorizerUser = computed<boolean>(
    () =>
      (this.isSelfRepresentedIndividual() &&
        !this.isSelfRepresentedOrganization()) ||
      this.isForm50Agent(),
  );

  /**
   * True when the effective user manages their own account rather than an organization: Form 50
   * Agents and self-represented individuals. These users see "Account Settings" in place of
   * Organization Settings, without the organization phone number and email fields.
   *
   * A self-represented *organization* is excluded: it shares the `SR` user type with an individual
   * but is a real organization, so it keeps the full Organization Settings experience.
   */
  readonly isAccountSettingsUser = computed<boolean>(
    () =>
      this.isForm50Agent() ||
      (this.isSelfRepresentedIndividual() &&
        !this.isSelfRepresentedOrganization()),
  );

  /**
   * The identity claims object stored centrally in a BehaviorSubject.
   */
  private identiyClaims$$ = new BehaviorSubject<IdentityClaims | null>(null);

  /**
   * The identity claims object exposed publicly as an Observable.
   */
  identiyClaims$ = this.identiyClaims$$.asObservable().pipe(
    filter((identityClaims: IdentityClaims | null) => {
      // Don't emit anything if we haven't set the claims object.
      return identityClaims !== null;
    }),
    map((identityClaims: IdentityClaims | null) => {
      // Ensures that downstream observables don't have to handle null.
      return identityClaims as IdentityClaims;
    }),
  );

  /**
   * The unique ID of the organization (Court) the user belongs to.
   */
  orgId$ = this.identiyClaims$.pipe(
    map((identityClaims: IdentityClaims) => {
      return identityClaims.fsx_organization_id;
    }),
  );

  /**
   * The unique ID of the logged-in user.
   */
  userId$ = this.identiyClaims$.pipe(
    map((identityClaims: IdentityClaims) => {
      return identityClaims.sub;
    }),
  );

  /**
   * The courthouse ID associated with the logged-in user.
   */
  courtHouseId$ = this.identiyClaims$.pipe(
    map((identityClaims: IdentityClaims) => {
      return identityClaims.extension_fsx_court_house_id;
    }),
  );

  /**
   * The full name of the logged-in user.
   */
  userFullName$ = this.identiyClaims$.pipe(
    map((identityClaims: IdentityClaims) => {
      return identityClaims.name;
    }),
  );

  /**
   * The username of the logged-in user.
   */
  signInName$ = this.identiyClaims$.pipe(
    map((identityClaims: IdentityClaims) => {
      return identityClaims.signinname;
    }),
  );

  /**
   * Whether the logged in user is a Court Admin or not.
   * - determined by checking the security functions.
   */
  isCourtAdmin$ = this.identiyClaims$.pipe(
    map((identityClaims: IdentityClaims) => {
      // List the valid court admin functions here...
      const courtAdminFunctions = ['OrgManager', 'Modify Org'];

      // Check if the logged-in user has any of the court admin functions
      const isCourtAdmin = courtAdminFunctions.reduce(
        (acc: boolean, cur: string) => {
          const found =
            acc ||
            identityClaims.extension_security_functions.indexOf(cur) >= 0;
          return found;
        },
        false,
      ); // Not a Court Admin until proven otherwise.

      // Return the resul
      return isCourtAdmin;
    }),
  );

  /**
   * A boolean value to determine whether or not the logged-in user
   * is a user type that can have bar numbers.
   */
  hasBarNumbers$ = this.identiyClaims$.pipe(
    map((identityClaims: IdentityClaims) => {
      // List the user types that have bar numbers here...
      const userTypesThatHaveBarNumbers: UserTypeEnum[] = [
        UserTypeEnum.Attorney,
        UserTypeEnum.Judge,
        UserTypeEnum.NonSubscribingJudgeUser,
        UserTypeEnum.NonSubscribingUser,
        UserTypeEnum.Clerk,
      ];

      // Check if the logged-in user is one of the user types that have bar numbers.
      const hasBarNumbers = userTypesThatHaveBarNumbers.includes(
        identityClaims.fsx_user_type,
      );

      // Return the result.
      return hasBarNumbers;
    }),
  );

  /**
   * The current identity claims, read synchronously.
   *
   * **Emulation-aware** — while an admin is emulating, this returns the overlaid (target) claims
   * written by {@link applyEmulation}, unlike `OAuthService.getIdentityClaims()`, which always
   * returns the admin's own token claims. Anything that derives a *user identity the server will
   * act on* (a draft's filerId, an org id, a user type) must read it from here, or the client sends
   * the admin's identity on a request the server scopes to the emulated user.
   *
   * Null until {@link setIdentityClaims} first runs (app bootstrap).
   */
  currentClaims(): IdentityClaims | null {
    return this.identiyClaims$$.value;
  }

  /**
   * A public method to allow for the setting of the identity claims object.
   *
   * @param claims The IdentityClaims object.
   */
  setIdentityClaims(claims: IdentityClaims): void {
    this.identiyClaims$$.next(claims);
    this.userId.set(claims.sub ?? null);
    this.orgId.set(claims.fsx_organization_id ?? null);
    this.userType.set(claims.fsx_user_type ?? null);
    const rawGroupId = claims.group_id == null ? null : String(claims.group_id);
    this.groupId.set(
      Object.values(UserGroup).includes(rawGroupId as UserGroup)
        ? (rawGroupId as UserGroup)
        : null,
    );
    this.organizationType.set(claims.fsx_organization_type ?? null);
    const rawOrganizationTypeId =
      claims.fsx_organization_type_id == null
        ? Number.NaN
        : Number(claims.fsx_organization_type_id);
    const knownOrganizationTypeIds: readonly number[] = Object.values(
      OrganizationTypeId,
    ).filter((value): value is number => typeof value === 'number');
    this.organizationTypeId.set(
      knownOrganizationTypeIds.includes(rawOrganizationTypeId)
        ? (rawOrganizationTypeId as OrganizationTypeId)
        : null,
    );
    this.efmId.set(claims.extension_fsx_efm_id ?? null);
  }

  /**
   * The real (admin) claims cached while an emulation overlay is active, restored on
   * {@link clearEmulation}. Null when not emulating.
   */
  private realClaims: IdentityClaims | null = null;

  /**
   * Overlays the emulated (target) user's identity onto the current claims so the app reflects the
   * target's context while emulating. The admin's real claims are cached for restoration. Data is
   * scoped to the target on the server via the impersonation header; this keeps the client's identity
   * state consistent with that.
   *
   * @param profile The emulated user's effective profile.
   */
  applyEmulation(profile: EmulatedProfile): void {
    const current = this.identiyClaims$$.value;
    if (current === null) {
      return;
    }

    // Cache the real claims only on the first overlay so re-applying does not lose them.
    if (this.realClaims === null) {
      this.realClaims = current;
    }

    this.setIdentityClaims({
      ...current,
      sub: profile.userUniqueId,
      fsx_organization_id: profile.organizationUniqueId,
      fsx_organization: profile.organizationName,
      fsx_user_type: profile.userType,
      group_id: String(profile.groupId),
      name: profile.fullName,
      // Only the target's own identity. A real login renders these claims with no fallback of its
      // own, so filling a missing one from anywhere — the admin's claims or the profile's full
      // name — would show identity the emulated user never sees.
      given_name: profile.firstName ?? '',
      family_name: profile.lastName ?? '',
      email: profile.email ?? '',
      // Cleared for the same reason as the name claims above, and it matters more: the emulated
      // profile carries no efmId, so leaving the admin's in place would let their C-Track id be
      // saved as an emulated Form 50 filing's party. Null instead fails the backend's check loudly.
      extension_fsx_efm_id: undefined,
    });
  }

  /**
   * Restores the admin's real identity claims after emulation ends.
   */
  clearEmulation(): void {
    if (this.realClaims === null) {
      return;
    }

    this.setIdentityClaims(this.realClaims);
    this.realClaims = null;
  }
}
